import { createContext, useContext, type ReactNode } from 'react';

const ReadOnlyContext = createContext(false);

/**
 * True when the surrounding region should not be editable.
 *
 * The `components/ui` form primitives (input, textarea, select, switch, checkbox,
 * radio-group, combobox, multi-select-combobox) read this themselves, so anything
 * inside a read-only region is non-editable without per-field wiring. Pages still
 * need to handle their own mutating actions (Save / Delete / Add / Remove buttons)
 * explicitly, since a button can't be classified automatically.
 *
 * The region wins over a control's own `disabled`/`readOnly` prop: those are OR-ed,
 * never overridden. Callers routinely pass a live boolean (`disabled={isLoading}`),
 * and letting a `false` there re-enable the field silently defeated the region.
 * `EditableRegion` is the only escape hatch.
 */
export function useIsReadOnly(): boolean {
  return useContext(ReadOnlyContext);
}

interface ReadOnlyProviderProps {
  readOnly: boolean;
  children: ReactNode;
}

/** Sets the read-only state for everything below it. */
export function ReadOnlyProvider({ readOnly, children }: ReadOnlyProviderProps) {
  return <ReadOnlyContext.Provider value={readOnly}>{children}</ReadOnlyContext.Provider>;
}

/**
 * Escape hatch for controls that stay live inside a read-only region — search
 * boxes, column filters, view/variant selectors, display toggles. Anything that
 * doesn't write to the record.
 *
 * Needed because the primitives go read-only by default, which is the right
 * default but does catch controls that only drive local view state.
 *
 * For third-party widgets, prefer their own read-only flag (Monaco's
 * `options.readOnly`, dnd-kit's `disabled`) over wrapping them: those keep the
 * content scrollable and selectable, which is the point of a browsable
 * read-only page.
 */
export function EditableRegion({ children }: { children: ReactNode }) {
  return <ReadOnlyProvider readOnly={false}>{children}</ReadOnlyProvider>;
}
